import { createContext, useContext, useEffect, useMemo, useSyncExternalStore, type ReactNode } from "react";
import { api, session } from "../api";
import { useAuth } from "../auth/AuthContext";
import { ChatStore, type ChatSnapshot } from "./store";

const ChatContext = createContext<ChatStore | null>(null);

/** One chat store (and chat stream) per signed-in device session, for the whole signed-in app. */
export function ChatProvider({ children }: { children: ReactNode }) {
  const { session: current } = useAuth();
  const userId = current?.userId;
  const deviceId = current?.deviceId;
  const store = useMemo(
    () => (userId && deviceId ? new ChatStore({ selfId: userId, deviceId, api, session }) : null),
    [userId, deviceId],
  );
  useEffect(() => {
    if (!store) return;
    store.start();
    const online = () => store.nudge();
    window.addEventListener("online", online);
    return () => {
      window.removeEventListener("online", online);
      store.stop();
    };
  }, [store]);
  return <ChatContext.Provider value={store}>{children}</ChatContext.Provider>;
}

const noSubscription = () => () => undefined;
const noSnapshot = () => null;

/** The chat store and its current state; null outside a signed-in session. */
export function useChat(): { store: ChatStore | null; chat: ChatSnapshot | null } {
  const store = useContext(ChatContext);
  const chat = useSyncExternalStore(store?.subscribe ?? noSubscription, store?.getSnapshot ?? noSnapshot);
  return { store, chat };
}
